import Handlebars from "handlebars";
import QRCode from "qrcode";
import { generateBarcodeDataURI } from "./barcode";
import { generatePromptPayPayload, type PromptPayType } from "./promptpay";

/**
 * Create a Handlebars template engine with registered helpers
 * for Thai locale formatting (currency, dates), math operations,
 * QR Code, Barcode, and PromptPay QR.
 */
export function createTemplateEngine(): typeof Handlebars {
  const hbs = Handlebars.create();

  // ── Currency ──────────────────────────────────────────────
  hbs.registerHelper("formatCurrency", (amount: number) => {
    if (amount == null) return "฿0.00";
    return (
      "฿" +
      Number(amount).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  });

  // ── Date DD/MM/YYYY ───────────────────────────────────────
  hbs.registerHelper("formatDate", (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("th-TH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  });

  // ── Thai short date  e.g. 31 มี.ค. 2569 ─────────────────
  hbs.registerHelper("formatThaiDate", (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  });

  // ── Thai long date  e.g. 31 มีนาคม 2569 ─────────────────
  hbs.registerHelper("formatThaiDateLong", (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  });

  // ── Math helpers ──────────────────────────────────────────
  hbs.registerHelper("multiply", (a: number, b: number) => Number(a) * Number(b));
  hbs.registerHelper("add", (a: number, b: number) => Number(a) + Number(b));
  hbs.registerHelper("subtract", (a: number, b: number) => Number(a) - Number(b));

  // ── Comparison helpers ────────────────────────────────────
  hbs.registerHelper("eq", (a: unknown, b: unknown) => a === b);
  hbs.registerHelper("neq", (a: unknown, b: unknown) => a !== b);
  hbs.registerHelper("gt", (a: number, b: number) => Number(a) > Number(b));
  hbs.registerHelper("gte", (a: number, b: number) => Number(a) >= Number(b));
  hbs.registerHelper("lt", (a: number, b: number) => Number(a) < Number(b));

  // ── Row number (1-based) ──────────────────────────────────
  hbs.registerHelper("rowNumber", (index: number) => index + 1);

  // ── Default value ─────────────────────────────────────────
  hbs.registerHelper("default", (value: unknown, fallback: unknown) =>
    value != null && value !== "" ? value : fallback,
  );

  // ── QR Code ───────────────────────────────────────────────
  /**
   * Generate QR Code inline image tag
   *
   * Usage:
   *   {{qrcode "https://example.com"}}
   *   {{qrcode invoiceUrl size=150}}
   *   {{qrcode someText size=120 color="#333333"}}
   *
   * Output: <img src="data:image/svg+xml;base64,..." width="150" height="150" style="display:block">
   *
   * หมายเหตุ: helper นี้เป็น async — ใช้ได้เฉพาะผ่าน renderTemplateAsync()
   */
  hbs.registerHelper(
    "qrcode",
    async (text: unknown, options: Handlebars.HelperOptions) => {
      const str = String(text ?? "");
      if (!str) return new Handlebars.SafeString("");

      // Clamp size: 10–500px (ป้องกัน DoS จาก size ใหญ่มาก)
      const rawSize = Number(options.hash?.size) || 150;
      const size: number = Math.min(Math.max(rawSize, 10), 500);
      // Validate colors ป้องกัน SVG attribute injection
      const rawColor = String(options.hash?.color ?? "#000000");
      const rawBgColor = String(options.hash?.bgColor ?? "#ffffff");
      const safeColorPattern = /^[a-zA-Z0-9#(),%./ -]+$/;
      const color: string = safeColorPattern.test(rawColor) ? rawColor : "#000000";
      const bgColor: string = safeColorPattern.test(rawBgColor) ? rawBgColor : "#ffffff";

      try {
        // toString type='svg' ไม่ใช้ Canvas — ทำงานบน Workers ได้
        const svgString: string = await QRCode.toString(str, {
          type: "svg",
          width: size,
          color: { dark: color, light: bgColor },
          errorCorrectionLevel: "M",
        });
        const b64 = btoa(svgString);
        const dataUri = `data:image/svg+xml;base64,${b64}`;
        return new Handlebars.SafeString(
          `<img src="${dataUri}" width="${size}" height="${size}" style="display:block" alt="QR Code">`,
        );
      } catch {
        return new Handlebars.SafeString(
          `<span style="color:red;font-size:11px">[QR Error]</span>`,
        );
      }
    },
  );

  // ── Barcode (Code128) ─────────────────────────────────────
  /**
   * Generate Code128 barcode inline image tag (SVG)
   *
   * Usage:
   *   {{barcode "INV-2024-001"}}
   *   {{barcode invoice.invoiceNo height=40}}
   *   {{barcode docId height=60 color="#1a1a1a"}}
   *
   * Output: <img src="data:image/svg+xml;base64,..." height="50" style="display:block" alt="...">
   */
  hbs.registerHelper("barcode", (text: unknown, options: Handlebars.HelperOptions) => {
    const str = String(text ?? "");
    if (!str) return new Handlebars.SafeString("");

    // Clamp height: 20–300px
    const rawHeight = Number(options.hash?.height) || 50;
    const height: number = Math.min(Math.max(rawHeight, 20), 300);
    // Validate color ป้องกัน SVG attribute injection
    const rawColor = String(options.hash?.color ?? "#000000");
    const color: string = /^[a-zA-Z0-9#(),%./ -]+$/.test(rawColor) ? rawColor : "#000000";

    try {
      const dataUri = generateBarcodeDataURI(str, height, color);
      return new Handlebars.SafeString(
        `<img src="${dataUri}" height="${height}" style="display:block" alt="${Handlebars.escapeExpression(str)}">`,
      );
    } catch {
      return new Handlebars.SafeString(
        `<span style="color:red;font-size:11px">[Barcode Error]</span>`,
      );
    }
  });

  // ── PromptPay QR Code ─────────────────────────────────────
  /**
   * Generate PromptPay QR Code inline image tag
   *
   * Usage:
   *   {{promptpay "0812345678"}}
   *   {{promptpay company.promptpayId amount=invoice.grandTotal}}
   *   {{promptpay "1234567890123" type="national_id" amount=500 size=200}}
   *
   * type: "phone" | "national_id" | "tax_id" | "ewallet"  (default: "phone")
   * amount: optional จำนวนเงิน (บาท) ถ้าไม่ระบุ = dynamic amount
   * size: ขนาด QR (px) default 150
   *
   * Output: <img src="data:image/svg+xml;base64,..." width="150" height="150" style="display:block">
   *
   * หมายเหตุ: helper นี้เป็น async — ใช้ได้เฉพาะผ่าน renderTemplateAsync()
   */
  hbs.registerHelper(
    "promptpay",
    async (id: unknown, options: Handlebars.HelperOptions) => {
      const idStr = String(id ?? "");
      if (!idStr) return new Handlebars.SafeString("");

      const type: PromptPayType = (options.hash?.type as PromptPayType) ?? "phone";
      const rawAmount = options.hash?.amount != null ? Number(options.hash.amount) : undefined;
      // Clamp amount: ต้องไม่เป็นลบ, ไม่เกิน 10,000,000 บาท
      const amount: number | undefined =
        rawAmount != null && rawAmount >= 0
          ? Math.min(rawAmount, 10_000_000)
          : undefined;
      // Clamp size: 10–500px
      const rawSize = Number(options.hash?.size) || 150;
      const size: number = Math.min(Math.max(rawSize, 10), 500);

      try {
        const payload = generatePromptPayPayload({ id: idStr, type, amount });
        const svgString: string = await QRCode.toString(payload, {
          type: "svg",
          width: size,
          errorCorrectionLevel: "M",
        });
        const b64 = btoa(svgString);
        const dataUri = `data:image/svg+xml;base64,${b64}`;
        return new Handlebars.SafeString(
          `<img src="${dataUri}" width="${size}" height="${size}" style="display:block" alt="PromptPay QR">`,
        );
      } catch {
        return new Handlebars.SafeString(
          `<span style="color:red;font-size:11px">[PromptPay Error]</span>`,
        );
      }
    },
  );

  return hbs;
}

// Module-level singleton — created once, reused across all requests
const engine = createTemplateEngine();

// ─── AST-based renderer (Workers-safe) ────────────────────────────────────────
//
// Cloudflare Workers disable V8 code generation from strings, which breaks
// Handlebars.compile() (it uses `new Function()` internally). Instead we use
// Handlebars.parse() — a pure LR parser that only builds an AST — and walk
// the AST ourselves. The registered helpers from `engine` are still used.
//
// Supported AST nodes: Program, ContentStatement, CommentStatement,
// MustacheStatement, BlockStatement, SubExpression, PathExpression, Hash,
// StringLiteral, NumberLiteral, BooleanLiteral, NullLiteral, UndefinedLiteral.
//
// Supported built-in block helpers: if, unless, each, with.

// Parsed AST cache — Workers isolates อยู่นาน, cache ช่วยลด parse overhead
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const astCache = new Map<string, any>();
const CACHE_MAX = 50;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getAst(htmlTemplate: string): any {
  const cached = astCache.get(htmlTemplate);
  if (cached) return cached;
  const ast = Handlebars.parse(htmlTemplate);
  if (astCache.size >= CACHE_MAX) astCache.clear();
  astCache.set(htmlTemplate, ast);
  return ast;
}

interface RenderScope {
  data: unknown;
  parent: RenderScope | null;
  root: unknown;
}

function htmlEscape(value: unknown): string {
  if (value == null) return "";
  if (value instanceof Handlebars.SafeString) return value.toString();
  const str = String(value);
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/`/g, "&#x60;");
}

function stringify(value: unknown): string {
  if (value == null) return "";
  if (value instanceof Handlebars.SafeString) return value.toString();
  return String(value);
}

function isTruthy(value: unknown): boolean {
  if (value == null || value === false || value === "" || value === 0) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

function lookupPath(path: any, scope: RenderScope): unknown {
  // PathExpression: { original, parts: string[], depth, this }
  let current: unknown;
  let workingScope: RenderScope | null = scope;

  // Handle @root, @key, @index — very minimal support
  if (path.data) {
    const name = path.parts?.[0];
    if (name === "root") {
      current = scope.root;
    } else {
      current = (scope.data as Record<string, unknown>)?.[`@${name}`];
    }
    for (let i = 1; i < path.parts.length; i++) {
      if (current == null) return undefined;
      current = (current as Record<string, unknown>)[path.parts[i]];
    }
    return current;
  }

  // depth — {{../var}} walks up N parents
  for (let d = 0; d < (path.depth || 0); d++) {
    workingScope = workingScope?.parent ?? null;
  }
  if (!workingScope) return undefined;

  current = workingScope.data;
  for (const part of path.parts) {
    if (current == null) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

async function evalExpression(expr: any, scope: RenderScope): Promise<unknown> {
  if (!expr) return undefined;
  switch (expr.type) {
    case "StringLiteral":
      return expr.value;
    case "NumberLiteral":
      return expr.value;
    case "BooleanLiteral":
      return expr.value;
    case "NullLiteral":
      return null;
    case "UndefinedLiteral":
      return undefined;
    case "PathExpression": {
      // If the first part matches a helper name with no params, treat as helper
      // invocation with 0 args (Handlebars default). Otherwise, lookup data.
      if (expr.parts?.length === 1 && !expr.depth && !expr.data) {
        const helper = (engine as any).helpers[expr.parts[0]];
        if (typeof helper === "function" && expr.parts[0] !== "this") {
          return helper.call(scope.data as object, {
            hash: {},
            data: { root: scope.root },
          });
        }
      }
      return lookupPath(expr, scope);
    }
    case "SubExpression": {
      return invokeHelper(expr, scope, null, null);
    }
    default:
      return undefined;
  }
}

async function evalHash(
  hash: any,
  scope: RenderScope,
): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  if (!hash || !hash.pairs) return out;
  for (const pair of hash.pairs) {
    out[pair.key] = await evalExpression(pair.value, scope);
  }
  return out;
}

async function invokeHelper(
  node: any,
  scope: RenderScope,
  blockProgram: any | null,
  inverseProgram: any | null,
): Promise<unknown> {
  const pathExpr = node.path;
  const name: string = pathExpr.original || pathExpr.parts?.[0];

  const params: unknown[] = [];
  for (const p of node.params || []) {
    params.push(await evalExpression(p, scope));
  }
  const hash = await evalHash(node.hash, scope);

  // Built-in block helpers
  if (blockProgram || inverseProgram) {
    if (name === "if") {
      const cond = params[0];
      return isTruthy(cond)
        ? await renderProgram(blockProgram, scope)
        : inverseProgram
          ? await renderProgram(inverseProgram, scope)
          : "";
    }
    if (name === "unless") {
      const cond = params[0];
      return !isTruthy(cond)
        ? await renderProgram(blockProgram, scope)
        : inverseProgram
          ? await renderProgram(inverseProgram, scope)
          : "";
    }
    if (name === "each") {
      const list = params[0];
      if (!isTruthy(list)) {
        return inverseProgram ? await renderProgram(inverseProgram, scope) : "";
      }
      let out = "";
      if (Array.isArray(list)) {
        for (let i = 0; i < list.length; i++) {
          const itemScope: RenderScope = {
            data: { ...(list[i] as object), "@index": i, "@first": i === 0, "@last": i === list.length - 1 },
            parent: scope,
            root: scope.root,
          };
          out += await renderProgram(blockProgram, itemScope);
        }
      } else if (list && typeof list === "object") {
        const keys = Object.keys(list as object);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          const itemScope: RenderScope = {
            data: {
              ...((list as Record<string, unknown>)[key] as object),
              "@key": key,
              "@index": i,
            },
            parent: scope,
            root: scope.root,
          };
          out += await renderProgram(blockProgram, itemScope);
        }
      }
      return out;
    }
    if (name === "with") {
      const ctx = params[0];
      if (!isTruthy(ctx)) {
        return inverseProgram ? await renderProgram(inverseProgram, scope) : "";
      }
      const newScope: RenderScope = { data: ctx, parent: scope, root: scope.root };
      return await renderProgram(blockProgram, newScope);
    }
    // Custom block helper: call with block options
    const helper = (engine as any).helpers[name];
    if (typeof helper === "function") {
      const options = {
        hash,
        data: { root: scope.root },
        fn: (ctx: unknown) =>
          renderProgram(blockProgram, {
            data: ctx ?? scope.data,
            parent: scope,
            root: scope.root,
          }),
        inverse: (ctx: unknown) =>
          inverseProgram
            ? renderProgram(inverseProgram, {
                data: ctx ?? scope.data,
                parent: scope,
                root: scope.root,
              })
            : Promise.resolve(""),
      };
      return await helper.call(scope.data as object, ...params, options);
    }
    return "";
  }

  // Mustache (inline) — custom helper
  const helper = (engine as any).helpers[name];
  if (typeof helper === "function") {
    const options = {
      hash,
      data: { root: scope.root },
    };
    return await helper.call(scope.data as object, ...params, options);
  }

  // Not a helper — treat as path lookup
  return lookupPath(pathExpr, scope);
}

async function renderStatement(stmt: any, scope: RenderScope): Promise<string> {
  switch (stmt.type) {
    case "ContentStatement":
      return stmt.value;
    case "CommentStatement":
      return "";
    case "MustacheStatement": {
      const result = await invokeHelper(stmt, scope, null, null);
      // `{{{var}}}` / `{{&var}}` → escaped=false → no escape
      return stmt.escaped === false ? stringify(result) : htmlEscape(result);
    }
    case "BlockStatement": {
      const result = await invokeHelper(stmt, scope, stmt.program, stmt.inverse);
      return stringify(result);
    }
    case "PartialStatement":
    case "PartialBlockStatement":
      // Partials not supported in this minimal renderer
      return "";
    default:
      return "";
  }
}

async function renderProgram(program: any, scope: RenderScope): Promise<string> {
  if (!program || !program.body) return "";
  let out = "";
  for (const stmt of program.body) {
    out += await renderStatement(stmt, scope);
  }
  return out;
}

async function renderAst(
  htmlTemplate: string,
  data: Record<string, unknown>,
): Promise<string> {
  const ast = getAst(htmlTemplate);
  const scope: RenderScope = { data, parent: null, root: data };
  return await renderProgram(ast, scope);
}

// ─── HTML document wrapper ────────────────────────────────────────────────────

function wrapHtmlDocument(body: string, cssStyles: string | null): string {
  return `<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <!--
    Sarabun Thai font via Google Fonts CDN.
    For server-side PDF (Puppeteer/Gotenberg), networkidle0 wait ensures the font loads.
    For offline / air-gapped use, replace with a base64 @font-face embedded in cssStyles.
  -->
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Sarabun:ital,wght@0,300;0,400;0,600;0,700;1,400&display=swap" rel="stylesheet">
  <style>
    /* ── Base print styles ── */
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
      font-size: 13px;
      color: #333;
      line-height: 1.6;
    }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 6px 8px; text-align: left; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-bold { font-weight: bold; }
    .border-b { border-bottom: 1px solid #ddd; }
    .mt-2 { margin-top: 8px; }
    .mt-4 { margin-top: 16px; }
    .mb-2 { margin-bottom: 8px; }
    .mb-4 { margin-bottom: 16px; }
    .py-1 { padding-top: 4px; padding-bottom: 4px; }
    .py-2 { padding-top: 8px; padding-bottom: 8px; }

    /* ── Print-specific ── */
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
      @page { size: A4 portrait; margin: 15mm; }
    }

    ${cssStyles || ""}
  </style>
</head>
<body>${body}</body>
</html>`;
}

/**
 * Render a Handlebars HTML template with data and wrap it in a
 * full HTML document (with CSS) ready for print / PDF generation.
 *
 * ใช้เมื่อ template ไม่มี async helpers (qrcode / promptpay)
 * ถ้ามี async helpers ให้ใช้ renderTemplateAsync() แทน
 */
export function renderTemplate(
  htmlTemplate: string,
  cssStyles: string | null,
  data: Record<string, unknown>,
): string {
  // In the Workers-safe renderer all helpers are async-capable; callers of
  // this sync entry point must await the returned promise. Prefer
  // `renderTemplateAsync` for new code.
  throw new Error(
    "renderTemplate (sync) is not supported in the Workers-safe engine. Use renderTemplateAsync.",
  );
}

/**
 * Render a Handlebars HTML template (async version)
 *
 * รองรับ async helpers: {{qrcode}}, {{promptpay}}
 * ใช้เสมอเมื่อ template อาจมี QR Code หรือ PromptPay QR
 * (barcode helper เป็น sync ใช้ได้ทั้ง 2 version)
 *
 * @example
 * const html = await renderTemplateAsync(template, css, data);
 */
export async function renderTemplateAsync(
  htmlTemplate: string,
  cssStyles: string | null,
  data: Record<string, unknown>,
): Promise<string> {
  const body = await renderAst(htmlTemplate, data);
  return wrapHtmlDocument(body, cssStyles);
}
